'use client';

import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { useGiftFeeBps } from '@/hooks/gifts/use-gift-fee';

/**
 * Self-contained "how gifting works" explainer sheet, surfaced from the
 * info button in the send-gift modal header. Owns its own translations + fee
 * lookup so the orchestrator only wires `open` / `onOpenChange`.
 */
export function GiftHowItWorksSheet({
  open,
  onOpenChange,
  showFee,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Cash flow presents its server quote; outcome-token send has no fee debit. */
  showFee: boolean;
}): React.ReactNode {
  const t = useTranslations('gift.send');
  const { feeBps } = useGiftFeeBps();

  return (
    <SilkSheet
      open={open}
      onOpenChange={onOpenChange}
      variant="detached"
      size="md"
      title={t('howItWorks.title')}
      description={t('howItWorks.intro')}
      footer={
        <div className="flex justify-end">
          <AnimatedButton type="button" onClick={(): void => onOpenChange(false)}>
            {t('howItWorks.close')}
          </AnimatedButton>
        </div>
      }
    >
      <ol className="flex flex-col gap-4">
        <li className="flex gap-3">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground-primary/10 text-caption font-semibold text-foreground-primary">
            1
          </span>
          <div className="flex flex-col gap-1">
            <p className="text-body font-medium">{t('howItWorks.step1Title')}</p>
            <p className="text-caption text-foreground-secondary">{t('howItWorks.step1Body')}</p>
          </div>
        </li>
        <li className="flex gap-3">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground-primary/10 text-caption font-semibold text-foreground-primary">
            2
          </span>
          <div className="flex flex-col gap-1">
            <p className="text-body font-medium">{t('howItWorks.step2Title')}</p>
            <p className="text-caption text-foreground-secondary">{t('howItWorks.step2Body')}</p>
          </div>
        </li>
        <li className="flex gap-3">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground-primary/10 text-caption font-semibold text-foreground-primary">
            3
          </span>
          <div className="flex flex-col gap-1">
            <p className="text-body font-medium">{t('howItWorks.step3Title')}</p>
            <p className="text-caption text-foreground-secondary">{t('howItWorks.step3Body')}</p>
          </div>
        </li>
        <li className="flex gap-3">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground-primary/10 text-caption font-semibold text-foreground-primary">
            4
          </span>
          <div className="flex flex-col gap-1">
            <p className="text-body font-medium">{t('howItWorks.step4Title')}</p>
            <p className="text-caption text-foreground-secondary">{t('howItWorks.step4Body')}</p>
          </div>
        </li>
      </ol>

      {showFee ? (
        <p className="rounded-control border border-border-primary bg-background-tertiary/40 p-3 text-caption text-foreground-secondary">
          {t('howItWorks.feeNote', { feePercent: feeBps / 100 })}
        </p>
      ) : null}
    </SilkSheet>
  );
}
